import { useEffect, useMemo, useState } from "react";
import { tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { colorFor } from '../lib/definitions/palette';
import { connectionStateInfo, freshnessOf, LIVENESS_COLOR, livenessSeverity } from '../lib/definitions/health';
import { featureCategoryLabel } from '../lib/definitions/settings';
import { trustTierInfo } from '../lib/definitions/trust';
import { formatNumber, relativeTime } from "../lib/format";
import { MqttBrokerTable } from "../components/MqttBrokerTable";
import { MeshcoreObserversSection } from "../components/MeshcoreObserversSection";
import type { MqttBroker } from "../api/types";

// The health rollup — the other of two nav destinations found
// advertised but dead (both #/packets and #/health silently rendered the
// Live Map; there was no `health` route in lib/router.ts at all). Built
// entirely from data that already exists elsewhere in this app — GET
// /api/health (already polled globally, AppData's `health`), GET
// /api/observers (AppData's `observers`), GET /api/mqtt/brokers (fetched
// here, same as MqttPage), and GET /api/features (AppData's `features`) —
// no new backend endpoint needed or requested.
//
// Stale-but-connected is the failure mode
// that matters — connected alone is a lying success flag and this whole
// platform exists partly to catch those. Every row below that has both a
// connection state AND a last-heard time gets ONE combined "Health"
// column computed by lib/definitions.ts's livenessSeverity() — connected
// alone is never enough to read as healthy; connected-but-stale renders in
// its own visibly different (amber) colour rather than inheriting
// "connected"'s green.
//
// GET /api/observers' `state`/`last_packet_at` used to be stale/unwired
// for kind:"transport"/"mqtt" entries (this page cross-referenced
// /api/health and /api/mqtt/brokers instead, flagged to the coordinator,
// 2026-09-20). Fixed at the source the same day — all seven observers now
// agree with their respective transport/broker objects — so this reads the
// field directly again; no workaround left to maintain.

function livenessLabel(connectionState: string | null | undefined, lastHeard: string | number | null | undefined): string {
  if (connectionState !== "connected") return connectionStateInfo(connectionState).label;
  const fresh = freshnessOf(lastHeard);
  if (fresh === "online") return "Healthy";
  if (fresh === "stale") return "Connected, stale";
  return "Connected, no recent data";
}

type Loadable<T> = T | null | "unavailable";

export function HealthPage() {
  const { effectiveTheme, health, healthError, observers, features } = useAppData();
  const [brokers, setBrokers] = useState<Loadable<MqttBroker[]>>(null);

  useEffect(() => {
    let cancelled = false;
    function poll() {
      tryApiGet<MqttBroker[]>("/api/mqtt/brokers")
        .then((r) => !cancelled && setBrokers(r ?? "unavailable"))
        .catch(() => !cancelled && setBrokers("unavailable"));
    }
    poll();
    const id = window.setInterval(poll, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const featuresByCategory = useMemo(() => [...features].sort((a, b) => a.category.localeCompare(b.category) || a.label.localeCompare(b.label)), [features]);
  const enabledCount = features.filter((f) => f.enabled).length;

  return (
    <div className="node-list-page">
      <h2 className="audit-page-title">Health</h2>

      <section className="page-section">
        <div className="page-section-title">Transports</div>
        {!health && !healthError && <div className="node-detail-note">Loading…</div>}
        {!health && healthError && <div className="node-detail-note">Couldn't load health status: {healthError}</div>}
        {health && (
          <>
            <div className="node-detail-note" style={{ marginBottom: 10 }}>
              Status: {health.status} · version {health.version} · up {Math.round(health.uptime_s / 60)} minute(s).
            </div>
            {health.transports.length === 0 ? (
              <div className="empty-state">No transports configured on this instance.</div>
            ) : (
              <div className="node-list-table-wrap">
                <table className="node-list-table">
                  <thead>
                    <tr>
                      <th>Transport</th>
                      <th>Kind</th>
                      <th>Connection</th>
                      <th>Last packet</th>
                      <th>Health</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.transports.map((t) => {
                      const conn = connectionStateInfo(t.state);
                      const severity = livenessSeverity(t.state, t.last_packet_at);
                      return (
                        <tr key={t.id}>
                          <td>{t.id}</td>
                          <td>{t.kind}</td>
                          <td>
                            <span className="badge" style={{ background: colorFor(conn.color, effectiveTheme) }}>
                              {conn.label}
                            </span>
                          </td>
                          <td>{t.last_packet_at ? relativeTime(t.last_packet_at) : "never"}</td>
                          <td>
                            <span className="badge" style={{ background: colorFor(LIVENESS_COLOR[severity], effectiveTheme) }}>
                              {livenessLabel(t.state, t.last_packet_at)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <section className="page-section">
        <div className="page-section-title">Observers</div>
        {observers.length === 0 ? (
          <div className="empty-state">No observers registered on this instance.</div>
        ) : (
          <div className="node-list-table-wrap">
            <table className="node-list-table">
              <thead>
                <tr>
                  <th>Observer</th>
                  <th>Trust tier</th>
                  <th>Connection</th>
                  <th>Last packet</th>
                  <th>Packets</th>
                  <th>Node sightings</th>
                  <th>Health</th>
                </tr>
              </thead>
              <tbody>
                {observers.map((o) => {
                  const conn = connectionStateInfo(o.state);
                  const tier = trustTierInfo(o.trust_tier);
                  const severity = livenessSeverity(o.state, o.last_packet_at);
                  const pollErrors = o.poll?.consecutive_errors ?? 0;
                  return (
                    <tr key={o.id}>
                      <td>{o.label || o.id}</td>
                      <td>
                        <span className="badge" style={{ background: colorFor(tier.color, effectiveTheme) }}>
                          {tier.short}
                        </span>
                      </td>
                      <td>
                        <span className="badge" style={{ background: colorFor(conn.color, effectiveTheme) }}>
                          {conn.label}
                        </span>
                        {pollErrors > 0 && (
                          <div className="mqtt-muted-suffix">
                            {pollErrors} consecutive poll error(s){o.poll?.last_error ? `: ${o.poll.last_error}` : ""}
                          </div>
                        )}
                      </td>
                      <td>{o.last_packet_at ? relativeTime(o.last_packet_at) : "never"}</td>
                      <td>{formatNumber(o.packets_total ?? 0)}</td>
                      <td>{formatNumber(o.node_sightings_total ?? 0)}</td>
                      <td>
                        <span className="badge" style={{ background: colorFor(LIVENESS_COLOR[severity], effectiveTheme) }}>
                          {livenessLabel(o.state, o.last_packet_at)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <MeshcoreObserversSection />

      <section className="page-section">
        <div className="page-section-title">MQTT brokers</div>
        <MqttBrokerTable brokers={brokers} />
      </section>

      <section className="page-section">
        <div className="page-section-title">
          Features ({enabledCount} of {features.length} enabled)
        </div>
        {features.length === 0 ? (
          <div className="empty-state">No feature registry reported yet.</div>
        ) : (
          <div className="node-list-table-wrap">
            <table className="node-list-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Unmet dependencies</th>
                </tr>
              </thead>
              <tbody>
                {featuresByCategory.map((f) => (
                  <tr key={f.id}>
                    <td>{f.label}</td>
                    <td>{featureCategoryLabel(f.category)}</td>
                    <td>
                      <span className="badge" style={{ background: f.enabled ? colorFor(LIVENESS_COLOR.healthy, effectiveTheme) : "var(--ink-muted)" }}>
                        {f.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td>{f.unmet.length > 0 ? f.unmet.join(", ") : "--"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
